// [COPIAR CÓDIGO]
//
/* GS://PROTOCOLO
 * Arquivo: src/pages/OrdersPage.js
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema GoldenSisters
 * Versão: 11.0 – FEAT: Reimpressão, Estorno PDV e Detalhes
 * Impacto da Versão: [src/pages/OrdersPage.js]
 * Data: 18/01/2026
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - [FEAT] Adicionada função `printReceipt` (portada do POS) para reimpressão de comprovantes.
 * - [FEAT] Modal de Detalhes atualizado: exibe Vendedor, Parcelamento e botão Imprimir.
 * - [FEAT] Implementada lógica de Estorno (Cancelamento) para vendas PDV com devolução de estoque (Batch).
 * - [FIX] Removida trava que impedia alteração de status de vendas PDV (apenas para cancelamento).
 */

import React, { useState, useEffect } from 'react';
import { db, functions, auth } from '../firebase/config'; 
import { collection, query, orderBy, onSnapshot, limit, doc, writeBatch, serverTimestamp, increment } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { updateOrderStatus } from '../services/OrderService';
import { Loader2, ShoppingCart, MapPin, Link as LinkIcon, Store, Smartphone, X, Filter, Calendar, Printer, User, CreditCard } from 'lucide-react';

// --- HELPERS ---

// [GS-FIX] Parser Universal de Datas
const parseFirestoreDate = (value) => {
    if (!value) return new Date(0); // Fallback para época se nulo
    if (value.toDate && typeof value.toDate === 'function') return value.toDate(); // Firestore Timestamp
    if (value instanceof Date) return value; // Objeto Date JS nativo
    if (typeof value === 'string' || typeof value === 'number') return new Date(value); // String ISO ou Epoch
    return new Date(0);
};

// [GS-FEAT] Função de Impressão de Comprovante (Reimpressão)
const printReceipt = (saleData) => {
    const printWindow = window.open('', '_blank', 'width=350,height=600');
    if (!printWindow) return;

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
    };

    const saleDate = parseFirestoreDate(saleData.createdAt || saleData.date).toLocaleString('pt-BR');

    // Lógica para tipo de pagamento detalhado
    let paymentDetail = saleData.paymentMethod;
    if (saleData.cardDetails) {
        paymentDetail = `${saleData.cardDetails.type === 'credit' ? 'Crédito' : 'Débito'} (${saleData.cardDetails.installments || 1}x)`;
    }

    const html = `
      <html>
      <head>
        <title>Comprovante GoldenSisters</title>
        <style>
          @page { margin: 0; }
          body { font-family: 'Courier New', monospace; font-size: 12px; margin: 0; padding: 5px; width: 80mm; color: #000; }
          .center { text-align: center; }
          .bold { font-weight: bold; }
          .line { border-bottom: 1px dashed #000; margin: 5px 0; }
          .table { width: 100%; border-collapse: collapse; }
          .table td { vertical-align: top; padding: 2px 0; }
          .qty { width: 15%; }
          .desc { width: 55%; }
          .val { width: 30%; text-align: right; }
          .totals-box { margin-top: 10px; border-top: 1px dashed #000; padding-top: 5px; }
          .row-sb { display: flex; justify-content: space-between; }
          .footer { margin-top: 15px; font-size: 10px; text-align: center; line-height: 1.4; }
        </style>
      </head>
      <body>
        <div class="center bold" style="font-size: 14px;">GOLDENSISTERS SEMIJOIAS</div>
        <div class="center bold" style="font-size: 10px;">(REIMPRESSÃO)</div>
        <div class="center">CNPJ: 55.137.637/0001-16</div>
        <div class="line"></div>
        <div>Data: ${saleDate}</div>
        <div>Cliente: ${saleData.customerName || 'Consumidor Final'}</div>
        <div class="line"></div>
        <table class="table">
          <tr class="bold" style="font-size:10px;">
            <td class="qty">QTD</td>
            <td class="desc">ITEM</td>
            <td class="val">TOTAL</td>
          </tr>
          ${(saleData.items || []).map(i => `
            <tr><td class="qty">${i.quantity}x</td><td class="desc">${i.name || i.productName}</td><td class="val">${formatCurrency((i.price || i.unitPrice) * i.quantity)}</td></tr>
          `).join('')}
        </table>
        <div class="totals-box">
          <div class="row-sb"><span>Subtotal:</span> <span>${formatCurrency(saleData.subtotal || saleData.totalValue)}</span></div>
          ${saleData.discountPoints > 0 ? `<div class="row-sb"><span>Desc. Pontos:</span> <span>-${formatCurrency(saleData.discountPoints)}</span></div>` : ''}
          ${saleData.discountManualAmount > 0 ? `<div class="row-sb"><span>Desc. Manual:</span> <span>-${formatCurrency(saleData.discountManualAmount)}</span></div>` : ''}
          <div class="line"></div>
          <div class="row-sb bold" style="font-size:14px; margin-top:5px;"><span>TOTAL:</span> <span>${formatCurrency(saleData.total || saleData.totalValue)}</span></div>
          
          <div class="center bold" style="margin-top:8px; font-size:12px;">Forma Pagto: ${paymentDetail}</div>
          
          <div class="line"></div>
        </div>
        <div class="footer">Rua Pedro de Toledo, 503<br>Vila Paulista - Cubatão - SP<br>CEP: 11510-090<br><br><span class="bold">Obrigado pela preferência!</span></div>
        <script>window.print();setTimeout(() => window.close(), 2000);</script>
      </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
};

// [GS-FEAT] Lógica de Prioridade de Ordenação (3 Níveis)
const getSortPriority = (item) => {
    const isApp = item.origin === 'app';
    const status = (item.status || '').toLowerCase();
    const isClosed = status.includes('concluido') || status.includes('completed') || status.includes('finalizado') || status.includes('cancelado') || status.includes('cancelled');

    if (isApp && !isClosed) return 0;
    if (!isApp) return 1;
    return 2;
};

const StatusBadge = ({ status }) => {
    let colorClasses = 'bg-gray-500 text-gray-100';
    let text = status;
    const st = (status || '').toLowerCase();

    if (st.includes('criado') || st === 'new') {
        colorClasses = 'bg-gray-600 text-gray-300 border border-gray-500';
        text = 'Criado (Novo)';
    } else if (st.includes('aprovado') || st.includes('paid')) {
        colorClasses = 'bg-cyan-500 text-cyan-900';
        text = 'Aprovado';
    } else if (st.includes('processando')) {
        colorClasses = 'bg-yellow-500 text-yellow-900';
        text = 'Processando';
    } else if (st.includes('retirada')) {
        colorClasses = 'bg-blue-400 text-blue-900';
        text = 'Aguard. Retirada';
    } else if (st.includes('enviado')) {
        colorClasses = 'bg-indigo-500 text-indigo-100';
        text = 'Enviado';
    } else if (st.includes('concluido') || st.includes('completed') || st.includes('finalizado')) {
        colorClasses = 'bg-green-500 text-green-100';
        text = 'Concluído';
    } else if (st.includes('cancelado') || st.includes('cancelled')) {
        colorClasses = 'bg-red-500 text-red-100';
        text = 'Cancelado';
    } else if (st.includes('pendente')) {
        colorClasses = 'bg-orange-400 text-orange-900';
        text = 'Pendente';
    }

    return (
        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${colorClasses}`}>
            {text}
        </span>
    );
};

const OrderDetailModal = ({ data, onClose }) => {
    if (!data) return null;
    
    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-gray-900 rounded-lg shadow-xl border border-gray-700 w-full max-w-lg overflow-hidden">
                <div className="p-4 border-b border-gray-700 flex justify-between items-center">
                    <h3 className="text-white font-bold flex items-center gap-2">
                        {data.origin === 'app' ? <Smartphone size={18} className="text-brand-gold" /> : <Store size={18} className="text-blue-400" />}
                        Detalhes do {data.origin === 'app' ? 'Pedido' : 'Venda'} #{data.displayId}
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
                </div>
                <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                    <div className="bg-gray-800 p-3 rounded">
                        <p className="text-xs text-gray-400 uppercase">Total</p>
                        <p className="text-2xl font-bold text-green-400">R$ {data.totalValue?.toFixed(2).replace('.', ',')}</p>
                        <div className="flex justify-between items-center mt-2">
                             <p className="text-xs text-gray-500">Status: {data.status}</p>
                             {/* [GS-FEAT] Exibir forma de pagamento/parcelamento se disponível */}
                             {data.paymentMethod && (
                                <p className="text-xs text-gray-300 flex items-center gap-1">
                                    <CreditCard size={12}/> {data.paymentMethod} 
                                    {data.cardDetails && ` (${data.cardDetails.type === 'credit' ? 'Crédito' : 'Débito'} ${data.cardDetails.installments || 1}x)`}
                                </p>
                             )}
                        </div>
                    </div>

                    {/* [GS-FEAT] Exibir Vendedor */}
                    {data.createdBy && (
                         <div className="flex items-center gap-2 text-sm text-gray-400 bg-gray-800/50 p-2 rounded border border-gray-700">
                            <User size={14} className="text-blue-400"/>
                            <span>Vendido por: <strong className="text-gray-200">{data.createdBy.name || 'Desconhecido'}</strong></span>
                         </div>
                    )}

                    <div>
                        <p className="text-sm font-bold text-white mb-2">Itens</p>
                        <div className="space-y-2">
                            {data.items?.map((item, idx) => (
                                <div key={idx} className="flex justify-between text-sm text-gray-300 border-b border-gray-800 pb-1">
                                    <span>{item.quantity}x {item.productName || item.title || item.name}</span>
                                    <span>R$ {(item.price || item.unitPrice || 0).toFixed(2)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    {data.customerName && (
                        <div className="text-sm text-gray-400 mt-4 pt-4 border-t border-gray-700">
                            <p><strong className="text-gray-300">Cliente:</strong> {data.customerName}</p>
                            <p><strong className="text-gray-300">Data:</strong> {data.createdAt?.toLocaleString('pt-BR')}</p>
                        </div>
                    )}
                </div>
                {/* [GS-FEAT] Botão de Reimpressão */}
                <div className="p-4 border-t border-gray-700 bg-gray-800 flex justify-end">
                    <button 
                        onClick={() => printReceipt(data)}
                        className="flex items-center gap-2 px-4 py-2 bg-brand-black text-white rounded hover:bg-gray-700 transition-colors text-sm font-bold"
                    >
                        <Printer size={16} /> Imprimir Comprovante
                    </button>
                </div>
            </div>
        </div>
    );
};

const OrdersPage = () => {
    const [filteredList, setFilteredList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedItem, setSelectedItem] = useState(null);

    // Estados de Filtro
    const getFirstDayOfMonth = () => new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
    const getToday = () => new Date().toISOString().split('T')[0];
    
    const [startDate, setStartDate] = useState(getFirstDayOfMonth());
    const [endDate, setEndDate] = useState(getToday());

    const [ordersApp, setOrdersApp] = useState([]);
    const [salesPos, setSalesPos] = useState([]);

    useEffect(() => {
        setLoading(true);
        // 1. Orders (App)
        const qOrders = query(collection(db, "orders"), orderBy("createdAt", "desc"), limit(200));
        const unsubOrders = onSnapshot(qOrders, (snapshot) => {
            const list = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    rawId: doc.id,
                    displayId: doc.id.substring(0, 7).toUpperCase(),
                    ...data,
                    createdAt: parseFirestoreDate(data.createdAt),
                    origin: 'app', 
                    totalValue: Number(data.totalValue || 0)
                };
            });
            setOrdersApp(list);
        });

        // 2. Sales (PDV)
        const qSales = query(collection(db, "sales"), orderBy("createdAt", "desc"), limit(200));
        const unsubSales = onSnapshot(qSales, (snapshot) => {
            const list = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    rawId: doc.id,
                    displayId: doc.id.substring(0, 7).toUpperCase(),
                    ...data,
                    createdAt: parseFirestoreDate(data.date || data.createdAt),
                    origin: 'store',
                    customerName: data.customerName || 'Cliente Balcão',
                    status: data.status || 'concluido',
                    totalValue: Number(data.total || data.totalValue || 0),
                    items: data.items || []
                };
            });
            setSalesPos(list);
        });

        return () => {
            unsubOrders();
            unsubSales();
        };
    }, []);

    // Unificação, Ordenação e Filtro
    useEffect(() => {
        let merged = [...ordersApp, ...salesPos];

        if (startDate && endDate) {
            const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
            const start = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0);

            const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
            const end = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999);

            merged = merged.filter(item => {
                const itemDate = item.createdAt || new Date(0);
                return itemDate >= start && itemDate <= end;
            });
        }

        merged.sort((a, b) => {
            const priorityA = getSortPriority(a);
            const priorityB = getSortPriority(b);
            if (priorityA !== priorityB) return priorityA - priorityB;
            const dateA = a.createdAt || new Date(0);
            const dateB = b.createdAt || new Date(0);
            return dateB - dateA;
        });

        setFilteredList(merged);
        if (ordersApp.length > 0 || salesPos.length > 0) setLoading(false);
    }, [ordersApp, salesPos, startDate, endDate]);


    const handleStatusChange = async (orderId, newStatus, currentStatus) => {
        if (newStatus === currentStatus) return;
        const target = filteredList.find(o => o.id === orderId);

        // [GS-MOD] Permitir cancelamento de venda PDV
        if (target && target.origin === 'store') {
            if (newStatus === 'cancelado') {
                const reason = prompt("Motivo do cancelamento (PDV):");
                if (!reason) return;
                
                if (!window.confirm(`Deseja realmente cancelar esta venda de R$ ${target.totalValue.toFixed(2)} e devolver os itens ao estoque?`)) return;

                try {
                    const batch = writeBatch(db);
                    const saleRef = doc(db, "sales", orderId);
                    
                    batch.update(saleRef, {
                        status: "cancelled",
                        cancelledAt: serverTimestamp(),
                        cancellationReason: reason,
                        cancelledBy: { uid: auth.currentUser?.uid || 'admin', name: auth.currentUser?.displayName || 'Admin' }
                    });

                    // Estorno de Estoque
                    if (target.items && Array.isArray(target.items)) {
                        target.items.forEach(item => {
                            if (item.productId) {
                                const productRef = doc(db, "products", item.productId);
                                batch.update(productRef, { stock: increment(item.quantity) });
                            }
                        });
                    }
                    
                    await batch.commit();
                    alert("Venda PDV cancelada e estoque estornado com sucesso.");
                } catch (e) {
                    alert("Erro ao cancelar venda PDV: " + e.message);
                    console.error(e);
                }
                return;
            } else {
                alert("Para Vendas PDV, apenas o CANCELAMENTO é permitido via painel. Outras alterações devem ser feitas no caixa.");
                return;
            }
        }

        // Lógica Original para Pedidos App
        let trackingCode = null;
        let cancellationReason = null;

        if (newStatus === 'enviado') {
            trackingCode = prompt("Código de rastreio:");
            if (!trackingCode) return;
        }
        
        if (newStatus === 'cancelado') {
            cancellationReason = prompt("Motivo do cancelamento:");
            if (!cancellationReason) return;

            try {
                const cancelOrderFn = httpsCallable(functions, 'cancelOrder');
                await cancelOrderFn({ orderId, reason: cancellationReason });
                alert("Pedido cancelado e estorno processado com sucesso.");
            } catch (err) {
                alert(`Erro ao cancelar: ${err.message}`);
                console.error(err);
            }
            return;
        }

        try {
            await updateOrderStatus(orderId, newStatus, trackingCode, cancellationReason);
        } catch (err) {
            alert(`Falha: ${err.message}`);
        }
    };

    const copyPaymentLink = (link) => {
        if (!link) return;
        navigator.clipboard.writeText(link).then(() => alert("Link copiado!")).catch(err => prompt("Link:", link));
    };

    const formatAddress = (order) => {
        if (order.origin === 'store') return <span className="text-gray-500 italic flex items-center gap-1"><Store size={12}/> Venda Local</span>;
        
        if (order.shippingOption?.serviceCode === 'PICKUP') {
            return (
                <span className="flex items-center text-yellow-500 font-medium">
                    <MapPin className="w-3 h-3 mr-1" /> Retirada na Loja
                </span>
            );
        }

        const addr = order.shippingAddress;
        if (!addr) return <span className="text-gray-500 italic">Endereço não informado</span>;

        return (
            <div className="flex flex-col text-xs max-w-[200px]">
                <span className="font-medium text-gray-200">{addr.street}, {addr.number}</span>
                <span className="text-gray-400">{addr.neighborhood}</span>
            </div>
        );
    };

    if (loading && filteredList.length === 0) {
        return (
            <div className="flex items-center justify-center h-full text-white">
                <Loader2 className="h-12 w-12 animate-spin text-brand-gold" />
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6">
            <h1 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                <ShoppingCart className="text-brand-gold"/> Central de Vendas
            </h1>

            {/* Barra de Filtros */}
            <div className="bg-gray-800 p-4 rounded-lg mb-4 flex flex-wrap items-center gap-4 border border-gray-700">
                <div className="flex items-center gap-2 text-gray-300">
                    <Filter size={16} />
                    <span className="text-sm font-bold uppercase">Filtros:</span>
                </div>
                <div className="flex items-center gap-2 bg-gray-900 px-3 py-1.5 rounded border border-gray-600">
                    <Calendar size={14} className="text-brand-gold"/>
                    <input 
                        type="date" 
                        value={startDate} 
                        onChange={(e) => setStartDate(e.target.value)}
                        className="bg-transparent border-none text-white text-xs focus:ring-0 p-0"
                    />
                    <span className="text-gray-500 text-xs">até</span>
                    <input 
                        type="date" 
                        value={endDate} 
                        onChange={(e) => setEndDate(e.target.value)}
                        className="bg-transparent border-none text-white text-xs focus:ring-0 p-0"
                    />
                </div>
                <div className="ml-auto text-xs text-gray-400">
                    Mostrando {filteredList.length} registros
                </div>
            </div>

            <div className="bg-gray-900 shadow-md rounded-lg overflow-hidden border border-gray-800">
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm text-left text-gray-300">
                        <thead className="text-xs text-gray-400 uppercase bg-gray-800 border-b border-gray-700">
                            <tr>
                                <th className="px-6 py-3">Origem</th>
                                <th className="px-6 py-3">ID / Data</th>
                                <th className="px-6 py-3">Cliente</th>
                                <th className="px-6 py-3">Resumo Itens</th>
                                <th className="px-6 py-3">Entrega / Local</th>
                                <th className="px-6 py-3">Total</th>
                                <th className="px-6 py-3">Status</th>
                                <th className="px-6 py-3 text-center">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800">
                            {filteredList.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="text-center text-gray-500 py-10">Nenhuma venda no período selecionado.</td>
                                </tr>
                            ) : (
                                filteredList.map((item) => {
                                    const isApp = item.origin === 'app';
                                    const paymentLink = item.paymentDetails?.initPoint;
                                    const showRecovery = isApp && paymentLink && (item.status === 'criado' || item.status === 'pendente');

                                    return (
                                        <tr key={item.id} className="hover:bg-gray-800/50 transition-colors cursor-pointer" onClick={() => setSelectedItem(item)}>
                                            <td className="px-6 py-4">
                                                {isApp ? (
                                                    <div className="flex items-center gap-1 text-brand-gold bg-brand-gold/10 px-2 py-1 rounded w-fit border border-brand-gold/20">
                                                        <Smartphone size={14} /> <span className="text-[10px] font-bold">APP</span>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-1 text-blue-400 bg-blue-400/10 px-2 py-1 rounded w-fit border border-blue-400/20">
                                                        <Store size={14} /> <span className="text-[10px] font-bold">LOJA</span>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 font-medium whitespace-nowrap">
                                                <div className="flex flex-col">
                                                    <span className="text-white">#{item.displayId}</span>
                                                    <span className="text-xs text-gray-500">
                                                        {item.createdAt ? item.createdAt.toLocaleDateString('pt-BR') : 'N/D'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-white font-medium">
                                                {item.customerName || 'Consumidor Final'}
                                            </td>
                                            <td className="px-6 py-4 text-xs text-gray-400 max-w-[200px] truncate">
                                                {item.items && item.items.length > 0 
                                                    ? `${item.items[0].quantity}x ${item.items[0].productName || item.items[0].name || item.items[0].title}` + (item.items.length > 1 ? ` +${item.items.length - 1} outros` : '')
                                                    : 'Sem itens'}
                                            </td>
                                            <td className="px-6 py-4">
                                                {formatAddress(item)}
                                            </td>
                                            <td className="px-6 py-4 font-bold text-white">
                                                R$ {item.totalValue?.toFixed(2).replace('.', ',')}
                                            </td>
                                            <td className="px-6 py-4">
                                                <StatusBadge status={item.status} />
                                            </td>
                                            <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                                                {isApp ? (
                                                    <div className="flex flex-col space-y-2 items-center">
                                                        <select
                                                            value={item.status}
                                                            onChange={(e) => handleStatusChange(item.id, e.target.value, item.status)}
                                                            className="w-full min-w-[120px] p-1 bg-gray-800 text-white text-xs rounded border border-gray-700"
                                                            disabled={item.status === 'concluido' || item.status === 'cancelado'}
                                                        >
                                                            <option value="criado">Criado</option>
                                                            <option value="pagamento_aprovado">Aprovado</option>
                                                            <option value="processando">Processando</option>
                                                            <option value="aguardando_retirada">Retirada</option>
                                                            <option value="enviado">Enviado</option>
                                                            <option value="concluido">Concluído</option>
                                                            <option value="cancelado">Cancelado</option>
                                                        </select>
                                                        {showRecovery && (
                                                            <button onClick={() => copyPaymentLink(paymentLink)} className="text-xs text-blue-400 hover:underline flex items-center gap-1">
                                                                <LinkIcon size={10} /> Link Pagto
                                                            </button>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col space-y-2 items-center">
                                                        {/* [GS-MOD] Dropdown simplificado para PDV (apenas cancelamento permitido via painel) */}
                                                         <select
                                                            value={item.status}
                                                            onChange={(e) => handleStatusChange(item.id, e.target.value, item.status)}
                                                            className="w-full min-w-[120px] p-1 bg-gray-800 text-white text-xs rounded border border-gray-700 disabled:opacity-50"
                                                            disabled={item.status === 'concluido' || item.status === 'cancelado'}
                                                        >
                                                            <option value="concluido">Concluído</option>
                                                            <option value="cancelado">CANCELAR VENDA</option>
                                                        </select>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {selectedItem && (
                <OrderDetailModal data={selectedItem} onClose={() => setSelectedItem(null)} />
            )}
        </div>
    );
};

export default OrdersPage;